import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API } from "utils/Helper";
import styled, { keyframes } from "styled-components";
import "swiper/css";
import "swiper/css/pagination";

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const SkeletonContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  height: 240px;
  overflow: hidden;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    height: 160px;
  }
`;

const SkeletonCard = styled.div`
  flex: 1;
  height: 100%;
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 600px 100%;
  border-radius: 20px;
  animation: ${shimmer} 1.4s infinite linear;
  
  @media (max-width: 600px) {
    &:nth-child(2) {
      display: none;
    }
  }
`;

const StyledSliderContainer = styled.div`
  width: 100%;
  height: 240px;
  margin-top: ${props => props.$type === 'home' ? '0px' : '10px'};
  margin-bottom: ${props => props.$type === 'home' ? '25px' : '49px'};
  
  @media (max-width: 600px) {
    height: 160px;
  }
  
  .swiper {
    width: 100%;
    height: 100%;
  }
  
  .swiper-slide {
    height: 100%;
    width: 50%; /* Default fallback width */
  }
`;

const BannerSlider = ({ type = "home" }) => {
  const { data: banners, isLoading } = useQuery({
    queryKey: ["websiteBanners", type],
    queryFn: async () => {
      const res = await axios.get(`${API}/website-banners?type=${type}&theme=bc`);
      return res.data?.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  if (isLoading) {
    return (
      <SkeletonContainer>
        <SkeletonCard />
        <SkeletonCard />
      </SkeletonContainer>
    );
  }

  if (!banners || banners.length === 0) {
    return null;
  }

  const bannerList = banners;

  return (
    <StyledSliderContainer $type={type}>
      <Swiper
        modules={[Pagination, Autoplay]}
        slidesPerView={2}
        spaceBetween={20}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        loop={bannerList.length > 1}  
        pagination={{
          clickable: true,
          el: ".custom-pagination",
          renderBullet: (index, className) => `
            <span class="${className}">
              <span class="progress-line"></span>
            </span>
          `,
        }}
        breakpoints={{
          100: { slidesPerView: 1, spaceBetween: 10 }, 
          600: { slidesPerView: 2, spaceBetween: 15 },  
          1200: { slidesPerView: 2, spaceBetween: 20 },  
        }}
      >
        {bannerList.map((banner, index) => {
          const imgSrc = banner.image_url.startsWith("http") || banner.image_url.startsWith("/uploads")
            ? (banner.image_url.startsWith("http") ? banner.image_url : `${API}${banner.image_url}`)
            : banner.image_url;

          return (
            <SwiperSlide key={index} className="slide-item">
              <a href={banner.link_url || "#"} style={{ display: "block", width: "100%", height: "100%" }}>
                <img 
                  src={imgSrc} 
                  alt={`Slide ${index + 1}`} 
                  className="slider-image" 
                  style={{
                    borderRadius: "20px",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center top",
                  }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div className="custom-pagination" style={{ marginTop: "10px" }}></div>
    </StyledSliderContainer>
  );
};

export default BannerSlider;
